'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Lock, Loader2, ShieldCheck } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { checkPassword, setAuthSession } from '@/lib/auth';

interface LoginFormValues {
  password: string;
}

export default function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({ mode: 'onSubmit' });

  async function onSubmit(data: LoginFormValues) {
    setIsLoading(true);
    setAuthError(null);

    const valid = await checkPassword(data.password);

    if (valid) {
      setAuthSession();
      router.push('/code-allocation-tool');
    } else {
      setAuthError('Incorrect password. Please try again.');
      setIsLoading(false);
    }
  }

  return (
    <div className="w-full max-w-sm animate-fade-in">
      {/* Brand header */}
      <div className="flex flex-col items-center gap-3 mb-8">
        <div className="flex items-center gap-3">
          <AppLogo size={40} />
          <span className="font-semibold text-xl tracking-tight text-foreground">
            CodeAllocator
          </span>
        </div>
        <p className="text-sm text-muted-foreground text-center">
          Internal SKU &amp; EAN code management
        </p>
      </div>

      {/* Login card */}
      <div className="card p-8 shadow-lg">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Lock size={16} className="text-primary" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-foreground">Sign in</h1>
            <p className="text-xs text-muted-foreground">Password-protected access</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
          {/* Password field */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter access password"
                className={`input-field pr-10 ${
                  errors.password || authError ? 'input-error' : ''
                }`}
                {...register('password', {
                  required: 'Password is required.',
                  minLength: { value: 1, message: 'Password is required.' },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1.5 text-xs text-error">{errors.password.message}</p>
            )}
            {authError && (
              <p className="mt-1.5 text-xs text-error animate-shake">{authError}</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Signing in…</span>
              </>
            ) : (
              <>
                <ShieldCheck size={16} />
                <span>Sign in</span>
              </>
            )}
          </button>
        </form>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-6">
        © 2026 CodeAllocator. Internal use only.
      </p>
    </div>
  );
}